import { memo } from 'react';
import { ChevronRight } from 'lucide-react';
import { IdealCustomerProfile } from '../../types';
import { Badge } from '../ui/Badge';
import { WIZARD_TEXT } from '../../constants/suggestionWizard';

interface ICPCardProps {
  icp: IdealCustomerProfile;
  index: number;
  totalCount: number;
  isDescExpanded: boolean;
  onExpandToggle: () => void;
  onSelect: () => void;
}

/**
 * Card component for displaying an ICP in the carousel.
 * Shows ICP details with expandable description.
 */
export const ICPCard = memo<ICPCardProps>(({
  icp,
  index,
  totalCount,
  isDescExpanded,
  onExpandToggle,
  onSelect,
}) => {
  // Build tags
  const tags: string[] = [];
  if (icp.customer_type) tags.push(icp.customer_type);
  if (icp.selling_motion) {
    tags.push(
      icp.selling_motion === 'plg'
        ? 'PLG'
        : icp.selling_motion === 'sales-led'
          ? 'Sales-Led'
          : 'Services'
    );
  }

  const hasDetails = icp.target_verticals?.length || icp.company_sizes?.length || icp.target_region;

  return (
    <div className="flex-shrink-0 w-[280px] min-h-[200px] snap-start bg-white border border-gray-200 rounded-xl overflow-hidden transition-all hover:border-gray-300 hover:shadow-md flex flex-col">
      {/* Card Content */}
      <div className="p-4 flex-1">
        {/* Header with name and counter */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="text-[14px] font-semibold text-gray-900 line-clamp-1">
            {icp.name}
          </h4>
          <span className="text-[10px] text-gray-400 flex-shrink-0">
            {index + 1}/{totalCount}
          </span>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex gap-1 mb-2.5">
            {tags.map((tag, idx) => (
              <Badge key={idx} variant="neutral" size="micro" shape="pill">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Description - clickable to expand */}
        {icp.solution_products && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onExpandToggle();
            }}
            className="cursor-pointer group/desc"
          >
            <p className={`text-[12px] text-gray-600 leading-relaxed mb-1 ${isDescExpanded ? '' : 'line-clamp-2'}`}>
              {icp.solution_products}
            </p>
            {icp.solution_products.length > 80 && (
              <span className="text-[10px] text-gray-400 group-hover/desc:text-gray-600">
                {isDescExpanded ? '↑ less' : '↓ more'}
              </span>
            )}
          </div>
        )}

        {/* Condensed Details */}
        {hasDetails && (
          <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-500 space-y-1">
            {icp.target_verticals?.length ? (
              <p>
                <span className="text-gray-400">Industries:</span>{' '}
                {icp.target_verticals.slice(0, 3).join(', ')}
                {icp.target_verticals.length > 3 && ` +${icp.target_verticals.length - 3}`}
              </p>
            ) : null}
            <p className="flex flex-wrap gap-x-3">
              {icp.company_sizes?.length ? (
                <span>
                  <span className="text-gray-400">Size:</span>{' '}
                  {icp.company_sizes.length === 1
                    ? icp.company_sizes[0]
                    : `${icp.company_sizes[0]}–${icp.company_sizes[icp.company_sizes.length - 1]}`}
                </span>
              ) : null}
              {icp.target_region && (
                <span>
                  <span className="text-gray-400">Region:</span>{' '}
                  {Array.isArray(icp.target_region)
                    ? (icp.target_region.length === 1
                        ? icp.target_region[0]
                        : `${icp.target_region.length} regions`)
                    : icp.target_region}
                </span>
              )}
            </p>
            {icp.titles && (
              <p className="line-clamp-1">
                <span className="text-gray-400">Titles:</span> {icp.titles}
              </p>
            )}
          </div>
        )}

        {/* Titles if no other details */}
        {!hasDetails && icp.titles && (
          <p className="text-[10px] text-gray-500 mt-2 line-clamp-1">
            <span className="text-gray-400">Titles:</span> {icp.titles}
          </p>
        )}
      </div>

      {/* Use this profile button */}
      <button
        onClick={onSelect}
        className="w-full px-4 py-2.5 bg-gray-900 text-[13px] font-medium text-white hover:bg-gray-800 transition-colors flex items-center justify-center gap-1.5 flex-shrink-0"
      >
        {WIZARD_TEXT.USE_PROFILE}
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
});

ICPCard.displayName = 'ICPCard';

export default ICPCard;
